import * as React from "react";
import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { Slider } from "primereact/slider";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";

import * as client from "../../../client";
import { readableLayer, perilCodeDisplayName, MIN_MULTIPLE } from "./util";
import {
  PerilEntry,
  applySliderChange,
  applyTargetTotal,
  buildInitialEntries,
  resetEntries,
  toQuotedTotal,
  toBaseTotal,
} from "./bulkPerilOverride";
import { formatExactMoneyDollars } from "../Money";
import styles from "./LayersCard.module.css";

type BulkPerilOverrideDialogProps = {
  layer: client.Layer | null;
  premium: client.LayerPremiumBreakdown | undefined;
  onHide: () => void;
  isPending: boolean;
  onApply: (
    layerId: string,
    targets: Array<{ perilCode: string; premium: number }>
  ) => Promise<void>;
};

const formatDollars = (n: number) =>
  formatExactMoneyDollars(n, { withRounding: true, withDollarSign: true });

export const BulkPerilOverrideDialog: React.FC<
  BulkPerilOverrideDialogProps
> = ({ layer, premium, onHide, isPending, onApply }) => {
  const [entries, setEntries] = useState<PerilEntry[]>([]);
  const [targetLocked, setTargetLocked] = useState(false);

  // Reset slider state whenever a different layer's dialog is opened. We
  // intentionally don't watch `premium` — if the backend refetches mid-edit we
  // keep the user's in-flight adjustments.
  useEffect(() => {
    if (layer != null) {
      setEntries(buildInitialEntries(premium));
      setTargetLocked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [layer?.layer_id]);

  const visible = layer != null;
  // Slider values are base (default-commission) premiums; the headline target
  // and the dropdown options are *quoted* premiums, so convert across the
  // commission factor here. Overrides we send stay in base space but carry cents
  // so the quoted total round-trips exactly. commissionPercent drives the exact
  // math; commissionFactor is only the float shown in the note.
  const commissionPercent = premium?.broker_commission_percentage ?? null;
  const commissionFactor = premium?.commission_adjustment_factor ?? 1;
  const baseTotal = entries.reduce((s, e) => s + e.value, 0);
  const totalPremium = toQuotedTotal(baseTotal, commissionPercent);

  const handleTargetChange = (raw: number | null | undefined) => {
    setEntries((prev) =>
      applyTargetTotal(
        prev,
        toBaseTotal(Math.round(raw ?? 0), commissionPercent)
      )
    );
  };

  const handleSliderChange = (perilCode: string, rawValue: number) => {
    setEntries((prev) =>
      applySliderChange(prev, perilCode, rawValue, targetLocked)
    );
  };

  const canApply = layer != null && entries.length > 0;

  const handleApply = async () => {
    if (!canApply) return;
    const targets = entries.map((e) => ({
      perilCode: e.perilCode,
      premium: e.value,
    }));
    await onApply(layer.layer_id!, targets);
    onHide();
  };

  return (
    <Dialog
      header={
        layer ? `Bulk Override — ${readableLayer(layer)}` : "Bulk Override"
      }
      visible={visible}
      onHide={onHide}
      draggable={false}
      style={{ width: "820px" }}
    >
      <div className={styles.addLayerForm}>
        <div className={styles.addLayerField}>
          <span>Target Layer Premium</span>
          <div className={styles.targetPremiumRow}>
            <i
              className={`pi ${targetLocked ? "pi-lock" : "pi-lock-open"} ${styles.bulkPerilLock}`}
              style={{ opacity: targetLocked ? 0.9 : 0.35 }}
              onClick={() => setTargetLocked((prev) => !prev)}
              title={
                targetLocked
                  ? "Unlock target — sliders move freely"
                  : "Lock target — other perils compensate"
              }
            />
            <InputNumber
              value={totalPremium}
              onValueChange={(e) => handleTargetChange(e.value)}
              mode="currency"
              currency="USD"
              locale="en-US"
              maxFractionDigits={0}
              placeholder="$0"
              inputStyle={{ width: "150px" }}
            />
            {(() => {
              const options: Array<{ label: string; price: number }> = [];
              if (layer?.target != null) {
                options.push({
                  label: `Target — ${formatDollars(layer.target)}`,
                  price: layer.target,
                });
              }
              for (const c of layer?.competitor_pricing ?? []) {
                if (c.price == null) continue;
                options.push({
                  label: `${c.carrier_name} — ${formatDollars(c.price)}`,
                  price: c.price,
                });
              }
              if (options.length === 0) return null;
              return (
                <Dropdown
                  value={null}
                  options={options}
                  optionLabel="label"
                  onChange={(e) => handleTargetChange(e.value.price)}
                  placeholder="Set to…"
                />
              );
            })()}
            {entries.length > 0 && (
              <Button
                label="Reset"
                icon="pi pi-undo"
                outlined
                size="small"
                onClick={() => {
                  setEntries(resetEntries);
                  setTargetLocked(false);
                }}
              />
            )}
          </div>
          {commissionFactor !== 1 && entries.length > 0 && (
            <span className={styles.bulkPerilCommissionNote}>
              Perils sum to {formatDollars(baseTotal)} at the default
              commission; the quoted target above applies the{" "}
              {commissionFactor.toFixed(4)}× commission adjustment.
            </span>
          )}
        </div>
        {entries.length === 0 ? (
          <div className={styles.layerPreview}>
            No perils on this layer to override.
          </div>
        ) : (
          <div className={styles.bulkPerilSliders}>
            <div className={`${styles.bulkPerilRow} ${styles.bulkPerilHeader}`}>
              <span />
              <span />
              <span />
              <span className={styles.bulkPerilHeaderCell}>New</span>
              <span className={styles.bulkPerilHeaderCell}>× AAL</span>
              <span className={styles.bulkPerilHeaderCell}>LR</span>
              <span className={styles.bulkPerilHeaderCell}>Current</span>
            </div>
            {entries.map((e) => (
              <div key={e.perilCode} className={styles.bulkPerilRow}>
                <i
                  className={`pi ${e.locked ? "pi-lock" : "pi-lock-open"} ${styles.bulkPerilLock}`}
                  style={{ opacity: e.locked ? 0.9 : 0.35 }}
                  onClick={() =>
                    setEntries((prev) =>
                      prev.map((p) =>
                        p.perilCode === e.perilCode
                          ? { ...p, locked: !p.locked }
                          : p
                      )
                    )
                  }
                  title={e.locked ? "Unlock" : "Lock"}
                />
                <span className={styles.bulkPerilLabel}>
                  {perilCodeDisplayName(e.perilCode)}
                </span>
                <Slider
                  value={e.value}
                  onChange={(ev) =>
                    handleSliderChange(e.perilCode, ev.value as number)
                  }
                  min={0}
                  max={e.max}
                  step={Math.max(1, Math.round(e.max / 100))}
                  disabled={e.locked}
                  className={styles.bulkPerilSlider}
                />
                <span
                  className={`${styles.bulkPerilValue} ${(() => {
                    const newR = Math.round(e.value);
                    const curR = Math.round(e.initial);
                    if (newR > curR) return styles.bulkPerilValueHigher;
                    if (newR < curR) return styles.bulkPerilValueLower;
                    return "";
                  })()}`}
                >
                  {formatDollars(e.value)}
                </span>
                <span className={styles.bulkPerilValue}>
                  {e.aal > 0 ? (e.value / e.aal).toFixed(2) : "—"}
                </span>
                <span
                  className={`${styles.bulkPerilValue} ${
                    e.value > 0 && e.value / e.aal < MIN_MULTIPLE
                      ? styles.bulkPerilValueLower
                      : ""
                  }`}
                >
                  {e.value > 0 ? ((e.aal / e.value) * 100).toFixed(1) : "—"}%
                </span>
                <span
                  className={`${styles.bulkPerilValue} ${
                    Math.round(e.value) !== Math.round(e.initial)
                      ? styles.bulkPerilValueMuted
                      : ""
                  }`}
                >
                  {formatDollars(e.initial)}
                </span>
              </div>
            ))}
          </div>
        )}
        <Button
          label="Apply"
          icon="pi pi-check"
          onClick={handleApply}
          disabled={!canApply || isPending}
          loading={isPending}
          style={{ marginTop: "8px" }}
        />
      </div>
    </Dialog>
  );
};
